import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Car, Eye, Users } from 'lucide-react';
import { api } from '../../utils/api';
import RideMatchModal from '../../components/Trips/RideMatchModal';

// TripLocationViewer component
const TripLocationViewer = React.memo(({ startLocation, endLocation, tripId }) => {
  const [showLocations, setShowLocations] = useState(false);
  const [startLocationName, setStartLocationName] = useState(null);
  const [endLocationName, setEndLocationName] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const getLocationName = async (location, type = '') => {
    let latitude, longitude;

    if (Array.isArray(location) && location.length >= 2) {
      longitude = location[0];
      latitude = location[1];
    } else if (location && typeof location === 'object') {
      latitude = location.latitude;
      longitude = location.longitude;
    } else {
      return null;
    }

    if (!latitude || !longitude) return null;

    try {
      const response = await api.post('/geocoding/reverse', {
        latitude: latitude,
        longitude: longitude,
      });
      return response?.placeName || response?.data?.placeName || 'Location not found';
    } catch (error) {
      return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
    }
  };

  const handleViewLocations = async () => {
    if (startLocationName && endLocationName) {
      setShowLocations(prev => !prev);
      return;
    }

    if (showLocations) {
      setShowLocations(false);
      return;
    }

    setIsLoading(true);

    try {
      const promises = [];

      if (!startLocationName && startLocation) {
        promises.push(
          getLocationName(startLocation).then(name => ({ type: 'start', name }))
        );
      }
      if (!endLocationName && endLocation) {
        promises.push(
          getLocationName(endLocation).then(name => ({ type: 'end', name }))
        );
      }

      if (promises.length > 0) {
        const results = await Promise.all(promises);
        let newStartName = startLocationName;
        let newEndName = endLocationName;

        results.forEach(result => {
          if (result.type === 'start') newStartName = result.name;
          else if (result.type === 'end') newEndName = result.name;
        });

        setStartLocationName(newStartName);
        setEndLocationName(newEndName);
        setIsLoading(false);

        setTimeout(() => setShowLocations(true), 100);
      } else {
        setShowLocations(true);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      setTimeout(() => setShowLocations(true), 100);
    }
  };

  const formatLocation = (location) => {
    let latitude, longitude;

    if (Array.isArray(location) && location.length >= 2) {
      longitude = location[0];
      latitude = location[1];
    } else if (location && typeof location === 'object') {
      latitude = location.latitude;
      longitude = location.longitude;
    } else {
      return 'Location not available';
    }

    if (!latitude || !longitude) return 'Location not available';
    return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="space-y-1 text-sm">
          <div className="flex items-center text-gray-600">
            <span className="h-2 w-2 bg-green-500 rounded-full mr-2"></span>
            <span className="font-mono text-xs">{formatLocation(startLocation)}</span>
          </div>
          <div className="flex items-center text-gray-600">
            <span className="h-2 w-2 bg-red-500 rounded-full mr-2"></span>
            <span className="font-mono text-xs">{formatLocation(endLocation)}</span>
          </div>
        </div>
        <button
          onClick={handleViewLocations}
          disabled={isLoading}
          className={`px-3 py-1 text-xs rounded-md transition-colors ${isLoading
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
            }`}
        >
          {isLoading ? (
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 border border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
              <span>Loading...</span>
            </div>
          ) : (
            <Eye className="h-3 w-3" />
          )}
        </button>
      </div>

      {showLocations && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 relative">
            <button
              onClick={() => setShowLocations(false)}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>

            <h3 className="text-lg font-semibold mb-4 text-gray-900">Trip Locations</h3>

            <div className="space-y-4">
              <div className="border-l-4 border-green-500 pl-4">
                <div className="flex items-center mb-1">
                  <span className="h-3 w-3 bg-green-500 rounded-full mr-2"></span>
                  <span className="text-sm font-medium text-gray-700">Start Location</span>
                </div>
                <p className="text-sm text-gray-900 font-medium">
                  {startLocationName || 'Loading...'}
                </p>
                <p className="text-xs text-gray-500 font-mono">
                  {formatLocation(startLocation)}
                </p>
              </div>

              <div className="border-l-4 border-red-500 pl-4">
                <div className="flex items-center mb-1">
                  <span className="h-3 w-3 bg-red-500 rounded-full mr-2"></span>
                  <span className="text-sm font-medium text-gray-700">End Location</span>
                </div>
                <p className="text-sm text-gray-900 font-medium">
                  {endLocationName || 'Loading...'}
                </p>
                <p className="text-xs text-gray-500 font-mono">
                  {formatLocation(endLocation)}
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowLocations(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

const ActiveTrips = () => {
  const [rideMatchTrip, setRideMatchTrip] = useState(null);
  // This list holds EVERY public-ride trip that is accepted or picked up, acting and
  // normal together. The filter lets an admin look at one kind.
  // Opened from the Acting Trips or Normal Trips page with ?type=acting|normal: start on
  // that kind and know where "back" goes. Opened any other way (no ?type), it shows all
  // and goes back to wherever the browser was.
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromType = (searchParams.get('type') || '').toLowerCase();
  const [typeFilter, setTypeFilter] = useState(
    fromType === 'acting' ? 'ACTING' : fromType === 'normal' ? 'NORMAL' : 'ALL',
  );
  const backTarget = fromType === 'acting' ? '/trips/acting' : fromType === 'normal' ? '/trips' : null;
  const backLabel = fromType === 'acting' ? 'Back to Acting Trips' : fromType === 'normal' ? 'Back to Normal Trips' : 'Back';
  const { data, isLoading, error, refetch } = useQuery(
    'active-trips',
    () => api.get('/trips/active/list'),
    {
      refetchInterval: 10000,
      refetchOnWindowFocus: true
    }
  );

  const activeTrips = React.useMemo(() => {
    if (!data) return [];
    if (data.data && Array.isArray(data.data)) return data.data;
    if (data.data?.data && Array.isArray(data.data.data)) return data.data.data;
    return [];
  }, [data]);

  const isActingTrip = (trip) => !!(trip?.isActingDriverTrip || trip?.serviceType === 'ACTING');

  const visibleTrips = React.useMemo(() => {
    if (typeFilter === 'ACTING') return activeTrips.filter(isActingTrip);
    if (typeFilter === 'NORMAL') return activeTrips.filter((t) => !isActingTrip(t));
    return activeTrips;
  }, [activeTrips, typeFilter]);

  const actingCount = React.useMemo(() => activeTrips.filter(isActingTrip).length, [activeTrips]);

  // A trip stays accepted / picked up until the driver ends it, so one whose driver never
  // did sits here for ever. Say how long, so a stuck trip stands out from a live one.
  const openForLabel = (trip) => {
    const since = Number(trip?.bookingTime) || Number(trip?.createdOn) || 0;
    const hours = since ? (Date.now() - since) / 3600000 : 0;
    if (hours < 12) return null;
    const days = Math.floor(hours / 24);
    return days >= 1 ? `Open for ${days}d ${Math.floor(hours - days * 24)}h` : `Open for ${Math.floor(hours)}h`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'ASSIGNED': { class: 'badge-info', text: 'Assigned' },
      'STARTED': { class: 'badge-primary', text: 'Started' },
      'IN_PROGRESS': { class: 'badge-primary', text: 'In Progress' }
    };

    const config = statusConfig[status] || { class: 'badge-info', text: status };
    return <span className={`badge ${config.class}`}>{config.text}</span>;
  };

  const getPaymentMethodBadge = (method) => {
    const methodConfig = {
      'CASH': { class: 'badge-success', text: 'Cash' },
      'CARD': { class: 'badge-primary', text: 'Card' },
      'UPI': { class: 'badge-info', text: 'UPI' },
      'WALLET': { class: 'badge-warning', text: 'Wallet' }
    };

    const config = methodConfig[method] || { class: 'badge-info', text: method };
    return <span className={`badge ${config.class}`}>{config.text}</span>;
  };

  const calculateFare = (trip) => {
    const baseFare = trip.minFare || 0;
    const adjustment = trip.fareAdjustment || 0;
    return baseFare + adjustment;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load active trips</p>
          <button onClick={() => refetch()} className="btn btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div>
              <button
                type="button"
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center mb-1"
                onClick={() => (backTarget ? navigate(backTarget) : navigate(-1))}
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> {backLabel}
              </button>
              <h1 className="page-title">Active Trips</h1>
              <p className="page-subtitle">
                Currently ongoing trips ({activeTrips.length}) — {actingCount} acting, {activeTrips.length - actingCount} normal
              </p>
              <div className="flex gap-2 mt-3">
                {[['ALL', 'All'], ['ACTING', 'Acting'], ['NORMAL', 'Normal rides']].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setTypeFilter(value)}
                    className={`px-3 py-1 rounded-full text-sm border ${typeFilter === value ? 'bg-blue-50 border-blue-400 text-blue-700 font-medium' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              {fromType !== 'normal' && (
                <Link to="/trips/acting" className="btn btn-outline">
                  All Acting Trips
                </Link>
              )}
              {fromType !== 'acting' && (
                <Link to="/trips" className="btn btn-outline">
                  All Normal Trips
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Active Trips Table */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Active Trips ({visibleTrips.length})
            </h3>
          </div>
        </div>
        <div className="card-body">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="loading-spinner"></div>
            </div>
          ) : visibleTrips.length === 0 ? (
            <div className="empty-state">
              <Car className="empty-state-icon" />
              <h3 className="empty-state-title">No Active Trips</h3>
              <p className="empty-state-description">
                There are currently no ongoing trips.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Trip ID</th>
                    <th className="table-header-cell">Passenger</th>
                    <th className="table-header-cell">Driver</th>
                    <th className="table-header-cell">Route</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Payment</th>
                    <th className="table-header-cell">Fare</th>
                    <th className="table-header-cell">Created</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {visibleTrips.map((trip) => (
                    <tr key={trip._id} className="table-row">
                      <td className="table-cell">
                        <div className="text-sm font-medium text-gray-900">
                          {trip.rideId}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          <span className={`text-xs px-1.5 py-0.5 rounded ${isActingTrip(trip) ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                            {isActingTrip(trip) ? 'Acting driver' : 'Normal ride'}
                          </span>
                          {openForLabel(trip) && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">{openForLabel(trip)}</span>
                          )}
                        </div>
                        {trip.otp && (
                          <div className="text-sm text-gray-500">
                            OTP: {trip.otp}
                          </div>
                        )}
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">
                          {trip.bookingForName || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {trip.bookingForPhone || 'N/A'}
                        </div>
                      </td>
                      <td className="table-cell">
                        {trip.driverId ? (
                          <div className="text-sm text-gray-900">
                            {trip.driverId.name}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">Not assigned</span>
                        )}
                      </td>
                      <td className="table-cell">
                        <TripLocationViewer
                          key={trip._id}
                          tripId={trip._id}
                          startLocation={trip.startLocation}
                          endLocation={trip.endLocation}
                        />
                      </td>
                      <td className="table-cell">
                        {getStatusBadge(trip.status)}
                      </td>
                      <td className="table-cell">
                        {getPaymentMethodBadge(trip.paymentMethod)}
                      </td>
                      <td className="table-cell">
                        <div className="text-sm font-medium text-gray-900">
                          {trip.actualFare ? (
                            `₹${trip.actualFare.toFixed(2)}`
                          ) : (
                            <span className="text-gray-400">₹{calculateFare(trip).toFixed(2)} (Est.)</span>
                          )}
                        </div>
                        {trip.finalDistance && (
                          <div className="text-sm text-gray-500 mt-1">
                            {Math.floor(trip.finalDistance * 10) / 10} km
                          </div>
                        )}
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">
                          {trip.bookingTime ? new Date(trip.bookingTime).toLocaleDateString() : 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {trip.bookingTime ? new Date(trip.bookingTime).toLocaleTimeString() : 'N/A'}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <Link
                            to={`/trips/${trip._id}`}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <Eye className="h-4 w-4" />
                          </Link>
                          <button
                              type="button"
                              onClick={() => setRideMatchTrip({ id: trip._id, rideId: trip.rideId })}
                              title="Ride match — who was offered this trip"
                              className="text-gray-500 hover:text-primary-600"
                            >
                              <Users className="h-4 w-4" />
                            </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {rideMatchTrip && (
        <RideMatchModal
          tripId={rideMatchTrip.id}
          rideId={rideMatchTrip.rideId}
          onClose={() => setRideMatchTrip(null)}
        />
      )}
    </div>
  );
};

export default ActiveTrips;
